/**
 * circuitAdapter.js — Maps RaceProX DB circuit data to the format
 * expected by the hologram engine (replaces nascarCircuits.js).
 *
 * The hologram engine expects a circuit object shaped like:
 * {
 *   id, label, title, location, trackNameText,
 *   laps, miles, flagUrl, flagLabel
 * }
 */

/**
 * Wrap raw SVG path-data (`d` attribute string) in a minimal `<svg>` document
 * so Three.js SVGLoader can parse it. If the value is already a full SVG
 * document (starts with `<`), return it as-is.
 *
 * @param {string} pathDataOrSvg
 * @returns {string}
 */
function wrapPathDataAsSvg(pathDataOrSvg) {
  const trimmed = pathDataOrSvg.trim();
  if (trimmed.startsWith('<')) return trimmed; // already a full SVG document
  // Raw path data — wrap in a viewBox-less SVG so SVGLoader can extract the path.
  // Scale up 12× because DB path coordinates are typically small.
  return `<svg xmlns="http://www.w3.org/2000/svg"><path d="${trimmed}" fill="none" stroke="black" transform="scale(12)"/></svg>`;
}

/**
 * @typedef {{
 *   id: string;
 *   label: string;
 *   title: string;
 *   location: string;
 *   trackNameText: string;
 *   laps: number;
 *   miles: number;
 *   flagUrl: string;
 *   flagLabel: string;
 *   svgSource: string | null;
 * }} HologramCircuit
 */

/**
 * Convert a DB circuits row into the hologram-engine format.
 * @param {object} dbCircuit - Row from the circuits table
 * @returns {HologramCircuit}
 */
export function adaptCircuitForHologram(dbCircuit) {
  const countryCode = (dbCircuit.country_code || '').toLowerCase();
  const flagUrl = countryCode
    ? `https://flagcdn.com/w320/${countryCode}.png`
    : '';
  const flagLabel = dbCircuit.country || '';

  // Extract laps and distance from stats JSONB if available
  let laps = 0;
  let miles = 0;
  if (Array.isArray(dbCircuit.stats)) {
    for (const stat of dbCircuit.stats) {
      const key = (stat.key || '').toLowerCase();
      if (key === 'laps') laps = parseFloat(stat.value) || 0;
      if (key === 'length' || key === 'distance') {
        const val = parseFloat(stat.value) || 0;
        const unit = (stat.unit || '').toLowerCase();
        miles = unit === 'km' ? val * 0.621371 : val;
      }
    }
  }

  const city = dbCircuit.city || '';
  const location = city
    ? `${city.toUpperCase()}, ${(dbCircuit.country || '').toUpperCase()}`
    : (dbCircuit.country || '').toUpperCase();

  return {
    id: dbCircuit.id,
    label: dbCircuit.name || 'Unknown Circuit',
    title: (dbCircuit.name || 'UNKNOWN CIRCUIT').toUpperCase(),
    location,
    trackNameText: (dbCircuit.name || '').toUpperCase(),
    laps,
    miles,
    flagUrl,
    flagLabel,
    svgSource: dbCircuit.layout_svg ? wrapPathDataAsSvg(dbCircuit.layout_svg) : null,
  };
}

/**
 * Format circuit race info for the start marker label.
 * @param {HologramCircuit} circuit
 * @returns {string}
 */
export function formatCircuitRaceInfo(circuit) {
  if (!circuit.laps && !circuit.miles) return circuit.label;
  const parts = [];
  if (circuit.laps) {
    parts.push(`${Number.isInteger(circuit.laps) ? circuit.laps : circuit.laps.toFixed(1)} laps`);
  }
  if (circuit.miles) {
    parts.push(`${Number.isInteger(circuit.miles) ? circuit.miles : circuit.miles.toFixed(1)} mi`);
  }
  return parts.join(' / ');
}

/**
 * Create a fallback circuit object when no DB circuit is available.
 * @param {string} [name]
 * @returns {HologramCircuit}
 */
export function createFallbackCircuit(name = 'Circuit') {
  return {
    id: 'fallback',
    label: name,
    title: name.toUpperCase(),
    location: '',
    trackNameText: name.toUpperCase(),
    laps: 0,
    miles: 0,
    flagUrl: '',
    flagLabel: '',
    svgSource: null,
  };
}
